import { Link, navigate } from "gatsby";
import React, { useEffect } from "react";
import { Alert } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Constants } from "../../../@types/Constants";
import ILoginError from "../../../@types/ILoginError";
import ILoginUser from "../../../@types/ILoginUser";
import {
  addCRMProfileCDP,
  addCRMBasicProfileCDP,
  login,
} from "../../../redux/slices/Member/member";
import { isDateEqualToOrBeforeApril2nd2023 } from "../../../services/helpers";
import * as auth0Client from "../../../utils/auth0Client";
import { Storage } from "../../../utils/storage";
import { PostMessage } from "../../../utils/webFramed";
import ErrorMessageAlert from "../ErrorMessageAlert/ErrorMessageAlert";
import SignupModal from "../SignupModal/SignupModal";
import { formGroup } from "./LoginForm.module.scss";
import {
  StyledForm,
  Title,
  TrackErrorMessage,
  WelcomeBlock,
} from "./LoginFormHelpers";
import { ILoginFormProps } from "./LoginFormProps";
const KEY_LOGIN_COOKIE = "Sonesta_login";

const LoginForm: React.FC<ILoginFormProps> = ({
  onForgotPassword,
  ...props
}) => {
  const dispatch = useDispatch();
  const path = props.location.pathname.split("/");
  const isCheckout = path[1] === "checkout";

  const { register, handleSubmit, errors } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  let isPasswordLessFeatureEnabled = false;
  if (process.env.GATSBY_PASSWORDLESS_LOGIN === "true") {
    isPasswordLessFeatureEnabled = true;
  }

  const [showSignupModal, setShowSignupModal] = React.useState(false);
  const fetchSavedData = (key: any) => {
    const data = Storage.GetLocalStorageValue(KEY_LOGIN_COOKIE);
    const parsedData = data ? JSON.parse(atob(data)) : null;
    return parsedData ? parsedData[key] : null;
  };
  const [message, setMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [passwordType, setPasswordType] = React.useState("password");
  const [showLabel, setShowLabel] = React.useState(false);
  const [username, setUsername] = React.useState(() => {
    return fetchSavedData("username") || "";
  });
  const [password, setPassword] = React.useState("");
  const [rememberMe, setRememberMe] = React.useState(() => {
    return !!username;
  });

  const saveLoginInStorage = (firstname: string) => {
    const loginDetails = {
      username: username,
      password: password,
      firstname: firstname,
    };
    Storage.SetLocalStorageValue(
      KEY_LOGIN_COOKIE,
      btoa(JSON.stringify(loginDetails))
    );
  };

  const clearLoginFromStorage = () => {
    Storage.ClearLocalStorageValue(KEY_LOGIN_COOKIE);
  };

  const reset = (e: any) => {
    e.preventDefault();
    setUsername("");
    setPassword("");
    setRememberMe(false);
    return false;
  };

  const onLogin = async (
    errObj: ILoginError | null,
    data: ILoginUser | Record<string, unknown>
  ) => {
    if (errObj?.errorMessage) {
      const isEqualToOrBeforeApril2nd = isDateEqualToOrBeforeApril2nd2023(
        errObj.lastEditDate
      );
      if (isEqualToOrBeforeApril2nd) {
        setMessage(
          "Your password is no longer valid. Please reset your password using the link below."
        );
      } else {
        setMessage(errObj.errorMessage);
      }
      setIsSubmitting(false);
    } else {
      const loginData = {
        type: "FRAMED_AUTHENTICATION",
        payload: {
          email: username,
          password: password,
        },
      };
      PostMessage(loginData);
      if (rememberMe) {
        //check response for first name
        saveLoginInStorage(data.name);
      } else {
        clearLoginFromStorage();
      }
      const tokenId = auth0Client.getToken();
      // localStorage.setItem(`userToken`, data.auth0Token)
      await dispatch(addCRMBasicProfileCDP(tokenId, true));
      if (props?.isEditReservation) {
        navigate("/edit-reservation/select-room");
      }
      await dispatch(login(data));
      setIsSubmitting(false);
      props.onSignIn && props.onSignIn();
    }
  };

  const onSubmit = async (data: any, e: any) => {
    e.preventDefault();
    setMessage("");
    setIsSubmitting(true);
    const response = await auth0Client.getCDPToken(username, password, onLogin);
  };

  const handleUsernameChange = (e: any) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: any) => {
    setPassword(e.target.value);
  };

  const handleRememberMe = (e: any) => {
    setRememberMe(e.target.checked);
  };

  const handleSignup = (e: any) => {
    if (isCheckout) {
      if (props.onSignUpLoad) {
        props.onSignUpLoad();
      }
      setShowSignupModal(true);
      e.preventDefault();
      return false;
    } else {
      navigate("/join-travel-pass");
    }
  };

  const handleHideSignupModal = () => {
    setShowSignupModal(false);
  };

  useEffect(() => {
    if (errors.password) {
      setShowLabel(false);
    }
  }, [errors.password]);

  const handleClearError = () => {
    setMessage("");
    errors.password ? setShowLabel(false) : setShowLabel(true);
  };
  const handleBlur = () => {
    errors.password ? setShowLabel(false) : setShowLabel(true);
  };
  const togglePassword = (event: any) => {
    event.stopPropagation();
    if (passwordType === "password") {
      setPasswordType("text");
      return;
    }
    setPasswordType("password");
  };
  return (
    <>
      <WelcomeBlock displayWelcome={props.displayWelcome}>
        <Title className={`signInTitle`} isFrom={props.isFrom}>
          {props.welcomeSubHeading || "Sign In"}
        </Title>
      </WelcomeBlock>

      <StyledForm
        onSubmit={handleSubmit(onSubmit)}
        class={`${props.className}`}
        isFrom={props.isFrom}
      >
        <>
          <Form.Group
            controlId={`email`}
            as={`div`}
            lg={props.controlColSize || 6}
            className={formGroup}
          >
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type={`string`}
              name={`email`}
              placeholder={`Enter email address`}
              value={username}
              onChange={handleUsernameChange}
              onFocus={handleClearError}
              autoComplete="on"
              ref={register({
                required: "This field is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$|[0-9]{9}/i,
                  message: "A valid Email address is required.",
                },
              })}
              isInvalid={errors.email}
              aria-invalid={errors.email ? "true" : "false"}
            />
            <Form.Control.Feedback type="invalid">
              <TrackErrorMessage
                message={errors.email && errors.email.message}
                field="email"
              />
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group
            controlId={`password`}
            as={`div`}
            lg={props.controlColSize || 6}
            className={formGroup}
          >
            <div className="password-container">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type={passwordType}
                name={`password`}
                placeholder={`Enter password`}
                value={password}
                ref={register({
                  required: "This field is required",
                })}
                onChange={handlePasswordChange}
                onFocus={handleClearError}
                onBlur={handleBlur}
                data-private={`lipsum`}
                //autoComplete="off"
                autoComplete="current-password"
                isInvalid={errors.password}
                aria-invalid={errors.password ? "true" : "false"}
              />
              {showLabel ? (
                passwordType === "password" ? (
                  <span onClick={togglePassword}>Show</span>
                ) : (
                  <span onClick={togglePassword}>Hide</span>
                )
              ) : null}
              <Form.Control.Feedback type="invalid">
                <TrackErrorMessage
                  message={errors.password && errors.password.message}
                  field="password"
                />
              </Form.Control.Feedback>
            </div>
          </Form.Group>
        </>
        {message ? (
          <ErrorMessageAlert
            errorType={Constants.ERRORSTYPE.TRAVELPASS}
            errorSubType={Constants.ERRORSSUBTYPE.TRAVELPASS.SIGNIN}
            message={message}
          >
            <Alert
              className="px-3 "
              id="error-msg-on-login"
              key={"danger"}
              variant={"danger"}
            >
              {message}
            </Alert>
          </ErrorMessageAlert>
        ) : null}
        <Row>
          <Form.Group
            controlId={`rememberUser`}
            className="remember-me-container"
            as={Col}
          >
            <Form.Check
              name={`rememberMe`}
              label="Remember me"
              checked={rememberMe}
              onChange={handleRememberMe}
            />
          </Form.Group>
        </Row>

        {props.isFrom === "checkout" || props.isFrom === "edit-reservation" ? (
          <>
            <div className={`form-actions`}>
              <Button
                type="submit"
                id="sign-in-to-travel-pass"
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? `Please wait...` : `SIGN IN`}
              </Button>
              <Link
                id="forgot-password-link"
                className={`help-link`}
                to={`/forgot-password`}
              >
                Forgot Password
              </Link>
              <div className={`join-message`}>
                <p>
                  Not a member?{" "}
                  <Link
                    to={`/join-travel-pass`}
                    id="join-travel-pass-link"
                    onClick={handleSignup}
                  >
                    Join Travel Pass
                  </Link>{" "}
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={`form-actions`}>
              <Button
                type="submit"
                id="sign-in-to-travel-pass"
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? `Please wait...` : `Sign in`}
              </Button>
            </div>
            <div className="help-link-container">
              <p
                id="forgot-password-link"
                className="help-link"
                onClick={
                  isPasswordLessFeatureEnabled
                    ? onForgotPassword
                    : () => (window.location.href = "/forgot-password")
                }
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    isPasswordLessFeatureEnabled
                      ? onForgotPassword()
                      : (window.location.href = "/forgot-password");
                    e.preventDefault();
                  }
                }}
                role="link"
                aria-label="forgot password"
              >
                Forgot Password
              </p>
            </div>
            <hr
              style={{
                border: "none",
                borderColor: "#d5d5d5",
                margin: "5px 0",
                background: "hsla(0, 0%, 0%, 0.5)",
              }}
            />
            <div className={`join-message`}>
              <p>
                Not a member?{" "}
                <Link
                  to={`/join-travel-pass`}
                  id="join-travel-pass-link"
                  onClick={handleSignup}
                >
                  Join Travel Pass
                </Link>{" "}
              </p>
            </div>
          </>
        )}
        {showSignupModal && (
          <SignupModal show={showSignupModal} onHide={handleHideSignupModal} />
        )}
      </StyledForm>
      {showSignupModal && (
        <SignupModal show={showSignupModal} onHide={handleHideSignupModal} />
      )}
    </>
  );
};

export default LoginForm;
